/**
 * Offline customer edits (docs/sync-conflict-resolution-plan.md Phase 4, D4/D5).
 *
 * A customer is the one record a till may edit offline. The detail page caches
 * the customer whenever it loads online; offline it reads that copy, and a save
 * updates the copy and queues a CUSTOMER UPDATE carrying the version the edit
 * started from. The server applies it if nothing changed meanwhile, otherwise
 * it raises a conflict for a Manager to resolve.
 */
import { posDb, deviceId, type CachedCustomer } from '@/db/pos-database';
import { decryptFromStorage, encryptForStorage } from '@/db/encrypted-access';
import { enqueue } from '@/services/sync-queue.service';
import { customersService, type CustomerDetail } from '@/services/customers.service';

/** Mirrors the API's OFFLINE_EDITABLE_CUSTOMER_FIELDS. Phone is never editable offline. */
export const OFFLINE_EDITABLE_CUSTOMER_FIELDS = [
  'name', 'email', 'address', 'notes', 'tags', 'date_of_birth', 'anniversary',
] as const;

export type OfflineCustomerEdit = Partial<
  Pick<CachedCustomer, 'name' | 'email' | 'address' | 'notes' | 'tags' | 'date_of_birth' | 'anniversary'>
>;

/** Keep the editable fields (and the version an offline edit starts from) for offline use. */
export async function cacheCustomerDetail(customer: CustomerDetail): Promise<void> {
  const row: CachedCustomer = {
    id: customer.id,
    name: customer.name,
    phone: customer.phone ?? null,
    email: customer.email ?? null,
    loyalty_points: customer.loyalty_points ?? 0,
    date_of_birth: customer.date_of_birth ?? null,
    anniversary: customer.anniversary ?? null,
    address: customer.address ?? null,
    notes: customer.notes ?? null,
    tags: customer.tags ?? [],
    version: customer.version ?? 1,
    synced_at: new Date().toISOString(),
  };
  await posDb.customers.put(await encryptForStorage(row, 'customers'));
}

export async function getCachedCustomer(id: string): Promise<CachedCustomer | undefined> {
  return decryptFromStorage(await posDb.customers.get(id));
}

/**
 * Re-read a customer this till has cached, after a Manager resolved a conflict
 * about it: whatever the outcome, the cached copy must match the server again.
 * Customers this till never cached are left alone.
 */
export async function refreshCachedCustomer(id: string): Promise<void> {
  if (!(await getCachedCustomer(id))) return;
  await cacheCustomerDetail(await customersService.getById(id));
}

/** The cached copy shaped as a CustomerDetail; stats the till cannot know offline are empty. */
export function toOfflineDetail(c: CachedCustomer): CustomerDetail {
  return {
    id: c.id,
    tenant_id: '',
    name: c.name,
    phone: c.phone ?? '',
    email: c.email,
    date_of_birth: c.date_of_birth ?? null,
    anniversary: c.anniversary ?? null,
    address: c.address ?? null,
    notes: c.notes ?? null,
    tags: c.tags ?? [],
    loyalty_points: c.loyalty_points,
    store_credit_balance: 0,
    visit_count: 0,
    total_spent: 0,
    avg_order_value: 0,
    last_visit_at: null,
    recent_bills: [],
    created_at: c.synced_at,
    updated_at: c.synced_at,
    version: c.version,
  };
}

/**
 * Save an edit while offline: update the cached copy and queue the change.
 * Only fields that actually changed are sent, with the values they changed
 * from (`base`) so a Manager sees a correct merge suggestion on a conflict.
 * Returns false when nothing changed.
 */
export async function saveCustomerOffline(id: string, edit: OfflineCustomerEdit): Promise<boolean> {
  const cached = await getCachedCustomer(id);
  if (!cached) throw new Error('This customer is not available offline. Reconnect to edit.');

  const base: Record<string, unknown> = {};
  const changes: Record<string, unknown> = {};
  for (const field of OFFLINE_EDITABLE_CUSTOMER_FIELDS) {
    if (!(field in edit)) continue;
    const next = edit[field] ?? null;
    const prev = cached[field] ?? null;
    if (JSON.stringify(next) === JSON.stringify(prev)) continue;
    base[field] = prev;
    changes[field] = next;
  }
  if (Object.keys(changes).length === 0) return false;

  await posDb.customers.put(
    await encryptForStorage({ ...cached, ...changes } as CachedCustomer, 'customers'),
  );
  await enqueue({
    client_id: crypto.randomUUID(),
    entity_type: 'CUSTOMER',
    operation: 'UPDATE',
    payload: { id, base_version: cached.version ?? 1, base, changes },
    device_id: deviceId,
    status: 'PENDING',
    created_at: new Date().toISOString(),
    retry_count: 0,
    last_error: null,
  });
  return true;
}
